import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Message, ChatContext } from "@/components/chat/types";
import { useAuth } from "@/providers/AuthProvider";
import { Json } from "@/integrations/supabase/types";

const entityLinkClasses: Record<string, string> = {
  candidate: "text-blue-600 dark:text-blue-400 bg-blue-500/10 hover:bg-blue-500/20",
  job: "text-green-600 dark:text-green-400 bg-green-500/10 hover:bg-green-500/20",
  company: "text-purple-600 dark:text-purple-400 bg-purple-500/10 hover:bg-purple-500/20",
  contact: "text-orange-600 dark:text-orange-400 bg-orange-500/10 hover:bg-orange-500/20",
};

export const useChatMessages = () => {
  const { t } = useTranslation();

  const makeWelcomeMessage = (): Message => ({
    id: "welcome",
    role: "assistant",
    content: t("chat.welcome"),
    timestamp: new Date(),
  });

  const [messages, setMessages] = useState<Message[]>(() => [makeWelcomeMessage()]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  // Get current context based on the route
  const getCurrentContext = (): ChatContext => {
    const path = location.pathname;
    const pathParts = path.split("/").filter(Boolean);
    
    let context: ChatContext = {
      page: pathParts[0] || "home",
    };
    
    if (pathParts.length > 1) {
      context.entityId = pathParts[1];
      context.entityType = pathParts[0].endsWith('s') 
        ? pathParts[0].slice(0, -1) 
        : pathParts[0];
    }
    
    //console.log("Current context:", context);
    return context;
  };

  // Create a new conversation in the database
  const createConversation = async () => {
    if (!user?.id) return null;
    
    try {
      // Get user's organization
      const { data: userOrg } = await supabase
        .from('user_organization')
        .select('organization_uuid')
        .eq('user_uuid', user.id)
        .single();
      
      if (!userOrg) {
        console.error("Could not find user organization");
        return null;
      }

      const context = getCurrentContext();
      
      // Convert context to a JSON compatible object
      const contextJson = JSON.parse(JSON.stringify(context)) as Json;
      
      // We need to explicitly define the type expected by the database
      const { data, error } = await supabase
        .from('chat_conversations')
        .insert({
          user_id: user.id,
          context: contextJson
        })
        .select()
        .single();
      
      if (error) throw error;
      
      //console.log("Created new conversation:", data.id);
      return data.id;
      
    } catch (error) {
      console.error("Error creating conversation:", error);
      return null;
    }
  };

  // Save a message to the database
  const saveMessage = async (message: Message, conversationId: string, resultsMetadata?: any) => {
    if (!conversationId) return;
    
    try {
      // Prepare the metadata with results if provided
      const metadata = resultsMetadata ? { results: resultsMetadata } : undefined;

      // We need to explicitly define the type expected by the database
      await supabase
        .from('chat_messages')
        .insert({
          conversation_id: conversationId,
          role: message.role,
          content: message.content as string,
          language: message.language,
          timestamp: message.timestamp.toISOString(),
          metadata: metadata
        });
      
      // Update conversation timestamp
      await supabase
        .from('chat_conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', conversationId);
        
    } catch (error) {
      console.error("Error saving message:", error);
    }
  };

  const handleEntityClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    
    if (target.classList.contains("entity-link")) {
      const entityType = target.getAttribute("data-entity-type");
      const entityId = target.getAttribute("data-entity-id");
      
      if (entityType && entityId) {
        // Determine the correct route based on entity type
        let route = "";
        
        switch (entityType) {
          case "candidate":
            route = `/candidates/${entityId}`;
            break;
          case "job":
            route = `/jobs/${entityId}`;
            break;
          case "company":
            route = `/companies/${entityId}`;
            break;
          case "contact":
            // For contacts, we navigate to the company page with a state param
            // to indicate which contact to highlight
            route = `/companies/${entityId}`;
            break;
        }
        
        if (route) {
          navigate(route);
          return true; // Indicate navigation happened
        }
      }
    }
    return false; // No navigation happened
  };

  // Get conversation history for the AI to use as context
  const getConversationHistory = (): Array<Message> => {
    // Return the last 5 messages (excluding the welcome message)
    // to provide context for follow-up questions
    return messages.filter(msg => msg.id !== "welcome").slice(-5);
  };

  const sendMessage = async (inputValue: string) => {
    if (inputValue.trim() === "" || isLoading) return;

    setIsLoading(true);

    // Ensure we have a conversation ID
    let conversationId = currentConversationId;
    if (!conversationId) {
      conversationId = await createConversation();
      setCurrentConversationId(conversationId);
    }

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content: inputValue,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    
    // Save user message to database
    if (conversationId) {
      await saveMessage(userMessage, conversationId);
    }

    try {
      const context = getCurrentContext();
      //console.log("Sending query:", userMessage.content);

      // Get conversation history for context
      const previousMessages = getConversationHistory();

      const { data, error } = await supabase.functions.invoke("ai-assistant", {
        body: {
          query: userMessage.content,
          context: context,
          conversationId: conversationId,
          previousMessages: previousMessages,
        },
      });

      if (error) {
        console.error("Error from AI assistant edge function:", error);
        throw new Error(error.message || "Error processing your request");
      }

      if (!data) {
        console.error("No response data received from edge function");
        throw new Error("No response data received");
      }

      //console.log("AI Response:", data);

      // Process links in the response to make them navigable
      let processedContent;
      
      // Process entity links into React elements structure
      const contentParts = [];
      let lastIndex = 0;
      const linkRegex = /\[(.*?)\]\((candidate|job|company|contact):([a-zA-Z0-9-]+)\)/g;
      let match;

      while ((match = linkRegex.exec(data.message)) !== null) {
        const [fullMatch, text, entityType, entityId] = match;
        
        // Add text before the match
        if (match.index > lastIndex) {
          contentParts.push(data.message.substring(lastIndex, match.index));
        }
        
        // Add the React element for the link
        contentParts.push({
          type: 'span',
          props: {
            className: `entity-link cursor-pointer rounded-sm px-1 py-0.5 font-medium transition-colors ${entityLinkClasses[entityType] ?? "text-primary bg-primary/10 hover:bg-primary/20"}`,
            'data-entity-type': entityType,
            'data-entity-id': entityId,
            children: text
          }
        });
        
        lastIndex = match.index + fullMatch.length;
      }

      // Add remaining text after the last match
      if (lastIndex < data.message.length) {
        contentParts.push(data.message.substring(lastIndex));
      }

      // If we found links, use the contentParts array, otherwise use the original message
      processedContent = contentParts.length > 0 ? contentParts : data.message;

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: processedContent,
        timestamp: new Date(),
        language: data.detectedLanguage,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      
      // Save assistant message to database with results metadata
      if (conversationId) {
        await saveMessage(assistantMessage, conversationId, data.results);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to get a response",
        variant: "destructive",
      });

      let errorMessage = t("chat.error");

      // If we're seeing errors consistently, suggest a different query
      if (messages.length > 2 && messages.some(m => typeof m.content === "string" && m.content.includes("error"))) {
        errorMessage += " You might try asking a simpler question or using different keywords.";
      }

      const errorMessageObj: Message = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: errorMessage,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessageObj]);
      
      // Save error message to database
      if (conversationId) {
        await saveMessage(errorMessageObj, conversationId);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = async () => {
    setMessages([makeWelcomeMessage()]);

    // Start a new conversation
    setCurrentConversationId(null);
  };

  const toggleFullScreen = () => {
    setIsFullScreen(prev => !prev);
  };

  return {
    messages,
    isLoading,
    isFullScreen,
    sendMessage,
    clearChat,
    toggleFullScreen,
    handleEntityClick,
  };
};
